import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'

import { DEMO_PLAYER_INPUT } from '../../../config/constants'
import { useRecalculateTestDriveScenarioMutation } from '../../../api/test_drive'
import { checkDecimal, checkDecimalsAfterDot } from '../../../utils/validation'

const inputLabelProps = {
  shrink: true,
  sx: {
    height: 16,
    lineHeight: '16px',
    letterSpacing: 0,
    transform: 'translate(16px, -5px) scale(.75)',
  },
}

const labelSx = { fontSize: 'inherit', lineHeight: 'inherit', letterSpacing: 'inherit' }
const inputSx = {
  minWidth: 0,
  '& .MuiInputLabel-root': { letterSpacing: 0 },
  '& .MuiOutlinedInput-input': { lineHeight: '24px' },
}

const DemoPlayerInputs = ({ scenario, onEdit, onRecalculated, onRecalculatingChange }: DemoPlayerInputsProps) => {
  const { t } = useTranslation()
  const compactLabels = useMediaQuery('(max-width:767px)')

  const product = scenario.order.invoices[0]?.products[0]
  const [ quantity, setQuantity ] = useState(String(product?.pieces ?? ''))
  const [ price, setPrice ] = useState(String(product?.unit_price ?? ''))
  const [ quantityInvalid, setQuantityInvalid ] = useState(false)
  const [ priceInvalid, setPriceInvalid ] = useState(false)
  const [ recalculate, { isLoading } ] = useRecalculateTestDriveScenarioMutation()
  const mounted = useRef(true)
  const confirmationInFlight = useRef(false)

  const quantityChanged = Number(quantity) !== Number(product?.pieces)
  const priceChanged = Number(price) !== Number(product?.unit_price)
  const hasChanges = quantityChanged || priceChanged

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  useEffect(() => {
    setQuantity(String(product?.pieces ?? ''))
    setPrice(String(product?.unit_price ?? ''))
  }, [ product?.pieces, product?.unit_price ])

  const save = async () => {
    if (isLoading || confirmationInFlight.current || !product) {
      return
    }

    const quantityValue = Number(quantity)
    const priceValue = Number(price)
    const quantityValid = checkDecimal(quantityValue, '') === true && quantityValue >= 1
    const decimalsValid = checkDecimalsAfterDot(price, '', 3) === true
    const priceProvided = price.trim() !== '' && Number.isFinite(priceValue)
    const priceValid = priceProvided && priceValue >= 0
    const priceInvalid = !decimalsValid || !priceValid

    if (!quantityValid || priceInvalid) {
      setQuantityInvalid(!quantityValid)
      setPriceInvalid(priceInvalid)
      return
    }

    setQuantityInvalid(false)
    setPriceInvalid(false)

    if (quantityValue === Number(product.pieces) && priceValue === Number(product.unit_price)) {
      return
    }

    onEdit()
    onRecalculatingChange(true)
    confirmationInFlight.current = true

    const result = await recalculate({
      slug: scenario.slug,
      items: [ { id: `item-${scenario.slug}`, quantity: quantityValue, unit_price: priceValue.toFixed(2) } ],
    })

    confirmationInFlight.current = false

    if (!mounted.current) {
      return
    }

    onRecalculatingChange(false)

    if ('error' in result) {
      setQuantityInvalid(quantityChanged)
      setPriceInvalid(priceChanged)
      return
    }

    onRecalculated()
  }

  return (
    <div
      className='demo-player-inputs'
      data-demo-allowed='player'
      onKeyDown={event => {
        if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
          event.preventDefault()
          save()
        }
      }}
      onBlur={event => {
        const nextTarget = event.relatedTarget as Node | null

        if (hasChanges && !event.currentTarget.contains(nextTarget)) {
          save()
        }
      }}
    >
      <TextField
        name={DEMO_PLAYER_INPUT.QUANTITY}
        value={quantity}
        label={<Typography component='span' sx={labelSx}>{t(compactLabels ? 'calc__demoPlayer__totalQuantityShort' : 'common__text__totalQuantity')}</Typography>}
        sx={inputSx}
        error={quantityInvalid}
        disabled={isLoading}
        onFocus={onEdit}
        onChange={event => {
          setQuantity(event.target.value)
          setQuantityInvalid(false)
        }}
        InputLabelProps={inputLabelProps}
        inputProps={{ inputMode: 'numeric', 'aria-busy': isLoading }}
        InputProps={{ endAdornment: <InputAdornment position='end' sx={{ padding: '0 2px' }}><Typography sx={{ letterSpacing: 0 }}>{t('units__text__items')}</Typography></InputAdornment> }}
      />
      <TextField
        name={DEMO_PLAYER_INPUT.PRICE}
        value={price}
        label={<Typography component='span' sx={labelSx}>{t(compactLabels ? 'calc__demoPlayer__pricePerPieceShort' : 'common__text__pricePerPiece')}</Typography>}
        sx={inputSx}
        error={priceInvalid}
        disabled={isLoading}
        onFocus={onEdit}
        onChange={event => {
          setPrice(event.target.value.replace(',', '.'))
          setPriceInvalid(false)
        }}
        InputLabelProps={inputLabelProps}
        inputProps={{ inputMode: 'decimal', 'aria-busy': isLoading }}
        InputProps={{ endAdornment: <InputAdornment position='end' sx={{ padding: '0 2px' }}><Typography sx={{ letterSpacing: 0 }}>{scenario.order.base_currency}</Typography></InputAdornment> }}
      />
    </div>
  )
}

export default DemoPlayerInputs
